import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { platformCoreNetworkApi } from '../../../api/platform/coreNetwork';
import { useAsyncData } from '../hooks/useAsyncData';
import { ConfirmDialog, DataTable, EmptyState, ErrorState, FeedbackBanner, InlineActions, KeyValueGrid, LoadingState, MvpCard, MvpTalentLayout, StatusBadge } from '../components/MvpTalentUi';
import { formatDateTime, formatList } from '../utils/format';

const InstanceDetailPage: React.FC = () => {
  const { instanceId = '' } = useParams();
  const [feedback, setFeedback] = useState<{ tone: 'success' | 'error'; message: string } | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data, loading, error, reload } = useAsyncData(async (signal) => {
    const [instance, memberships, work] = await Promise.all([
      platformCoreNetworkApi.getInstance(instanceId, { signal }),
      platformCoreNetworkApi.getInstanceMemberships(instanceId, {}, { signal }),
      platformCoreNetworkApi.listWorkRequests({ instanceId, limit: 10, page: 1 }, { signal }),
    ]);
    return { instance, memberships, work };
  }, [instanceId]);

  return (
    <MvpTalentLayout title={data?.instance.instanceName || `Instance ${instanceId}`} subtitle="Instance-level memberships, active work, and Core Network enablement.">
      {feedback ? <FeedbackBanner tone={feedback.tone} message={feedback.message} onDismiss={() => setFeedback(null)} /> : null}
      {loading ? <LoadingState label="Loading instance detail..." /> : null}
      {error ? <ErrorState message={error} onRetry={reload} /> : null}
      {data ? (
        <>
          <MvpCard title="Instance overview">
            <KeyValueGrid items={[
              { label: 'Instance ID', value: data.instance.id },
              { label: 'Slug', value: data.instance.instanceUniqueId || '-' },
              { label: 'Core Network', value: <StatusBadge status={data.instance.mvpTalentEnabled ? 'active' : 'disabled'} /> },
              { label: 'Memberships', value: data.instance.membershipCount || data.memberships.length },
              { label: 'Requests', value: data.instance.requestCount || data.work.pagination.total },
            ]} />
            <InlineActions>
              <button type="button" className="mvp-button" onClick={() => setDialogOpen(true)}>{data.instance.mvpTalentEnabled ? 'Disable Core Network' : 'Enable Core Network'}</button>
            </InlineActions>
          </MvpCard>

          <MvpCard title="Talent memberships">
            {data.memberships.length ? (
              <DataTable columns={['Talent', 'Status', 'Role', 'Request types', '']}>
                {data.memberships.map((membership) => (
                  <tr key={membership.id}>
                    <td>{membership.member ? <Link to={`/command-center/mvp-talent/talent/${membership.member.id}`}>{membership.member.displayName}</Link> : membership.memberId}</td>
                    <td><StatusBadge status={membership.status} /></td>
                    <td>{membership.role || '-'}</td>
                    <td>{formatList(membership.allowedRequestTypesJson)}</td>
                    <td><Link to={`/command-center/mvp-talent/talent/${membership.memberId}`}>Open talent</Link></td>
                  </tr>
                ))}
              </DataTable>
            ) : <EmptyState title="No memberships" message="No talent memberships were returned for this instance." />}
          </MvpCard>

          <MvpCard title="Active and historical work">
            {data.work.rows.length ? (
              <DataTable columns={['Request', 'Type', 'Status', 'Due', '']}>
                {data.work.rows.map((request) => (
                  <tr key={request.id}>
                    <td>{request.title || `Request #${request.id}`}</td>
                    <td>{request.requestType}</td>
                    <td><StatusBadge status={request.status} /></td>
                    <td>{formatDateTime(request.deadlineAt)}</td>
                    <td><Link to={`/command-center/mvp-talent/work/${request.id}`}>Open</Link></td>
                  </tr>
                ))}
              </DataTable>
            ) : <EmptyState title="No work requests" message="This instance has not returned any platform-visible Core Network work yet." />}
          </MvpCard>
        </>
      ) : null}

      <ConfirmDialog
        open={dialogOpen}
        title={data?.instance.mvpTalentEnabled ? 'Disable Core Network for this instance' : 'Enable Core Network for this instance'}
        description="This updates the existing backend instance setting. It does not create a frontend-only flag."
        confirmLabel={data?.instance.mvpTalentEnabled ? 'Disable instance' : 'Enable instance'}
        confirmTone={data?.instance.mvpTalentEnabled ? 'danger' : 'default'}
        onClose={() => setDialogOpen(false)}
        onConfirm={async ({ reason }) => {
          if (!data) return;
          try {
            await platformCoreNetworkApi.updateInstanceSettings(instanceId, { mvpTalentEnabled: !data.instance.mvpTalentEnabled, reason });
            setFeedback({ tone: 'success', message: 'Instance setting updated.' });
            await reload();
          } catch (mutationError: any) {
            setFeedback({ tone: 'error', message: mutationError?.message || 'Unable to update the instance setting.' });
            throw mutationError;
          }
        }}
      />
    </MvpTalentLayout>
  );
};

export default InstanceDetailPage;
